// frontend/src/infrastructure/auth/currentUser.ts
//
// Odczyt tożsamości zalogowanego operatora z JWT. Warstwa infrastruktury:
// jedyne miejsce, które wie, że „autor” notatki / zgłoszenia pochodzi z tokena
// (`pwa_jwt`, zaszyfrowany AES-GCM — ten sam wpis, którego używa reszta
// aplikacji), a nie z osobnego stanu UI.
//
// Podpisu NIE weryfikujemy — to robi backend przy każdym żądaniu. Tu potrzebny
// jest wyłącznie `sub` / `userId` z payloadu, żeby dołączyć `authorId` do
// operacji kolejkowanej offline.

import { decryptFromStore } from '../../utils/secureStorage'

/** Klucz localStorage z zaszyfrowanym JWT — spójny z `src/api.ts` i resztą repo. */
const LS_KEY_JWT = 'pwa_jwt'

/** Dekoduje sekcję `payload` JWT (base64url) bez weryfikacji podpisu. */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.')
  if (parts.length !== 3) return null

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const parsed: unknown = JSON.parse(atob(base64))
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, unknown>)
      : null
  } catch {
    return null
  }
}

/**
 * Identyfikator zalogowanego operatora do przypisania jako autor notatki /
 * zgłoszenia. Kolejność pól payloadu: `sub` -> `userId` -> `id`.
 *
 * Zwraca `null`, gdy tokena brak, jest nieczytelny albo nie niesie
 * identyfikatora — wtedy wpis zapisujemy jako anonimowy (backend to dopuszcza,
 * `authorId` jest nullable). Funkcja nigdy nie rzuca.
 */
export async function readCurrentAuthorId(): Promise<string | null> {
  let token: string | null
  try {
    token = await decryptFromStore(LS_KEY_JWT)
  } catch {
    return null
  }
  if (!token) return null

  const payload = decodeJwtPayload(token)
  if (!payload) return null

  const candidate = payload.sub ?? payload.userId ?? payload.id
  if (typeof candidate === 'string' && candidate.trim() !== '') return candidate.trim()
  if (typeof candidate === 'number' && Number.isFinite(candidate)) return String(candidate)
  return null
}
